import { Box, Typography, SxProps } from '@mui/material';
import parse, { HTMLReactParserOptions } from 'html-react-parser';
import { useRouter } from 'next/router';
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import DocumentSearch from '@components/search/DocumentSearch';
import { DocumentSearchTarget, DocumentSearchText } from '@models/searchable';
import useAmplitude from '@utils/hooks/useAmplitude';
import useReadPosition from '@utils/hooks/useReadPosition';
import { sxs } from '@utils/mui/sx';
import { sanitize, SanitizeOptions } from '@utils/parsing';
import { TrackingEventName } from '@utils/types/amplitude/events';

interface DisplayFullTextProps {
    containerSx?: SxProps;
    contentSx?: SxProps;
    dataTestId?: string;
    parseOptions?: HTMLReactParserOptions;
    sanitizeOptions?: SanitizeOptions;
    searchControls?: ReactNode;
    searchTarget?: DocumentSearchTarget;
    searchTargetId?: string;
    setSearchTarget?: (target?: DocumentSearchTarget) => void;
    unsanitizedContent: string;
    withSearch?: boolean;
}

const contentSx: SxProps = {
    '& a': {
        color: 'primary.main',
        textDecoration: 'underline',
    },
    '& blockquote': {
        border: 0,
        borderColor: 'grey.600',
        borderLeft: 0.75,
        borderStyle: 'solid',
        margin: 0,
        paddingLeft: 3,
    },
    textAlign: 'justify',
};

const DisplayFullText: FC<DisplayFullTextProps> = ({
    containerSx = {},
    contentSx: contentSxOverride = {},
    dataTestId = 'display-full-text',
    parseOptions,
    sanitizeOptions,
    searchControls,
    searchTarget,
    searchTargetId,
    setSearchTarget,
    unsanitizedContent,
    withSearch,
}) => {
    const { track } = useAmplitude();
    const { scrolledHalfway, scrolledToBottom } = useReadPosition();
    const { pathname } = useRouter();

    const sanitizedContent = sanitize(unsanitizedContent, sanitizeOptions);
    const parsedAndSanitizedContent = useMemo(
        () => parse(sanitizedContent, parseOptions),
        [parseOptions, sanitizedContent],
    );

    const [displayedContent, setDisplayedContent] =
        useState<DocumentSearchText>(parsedAndSanitizedContent);

    const handleContentUpdate = useCallback((content: DocumentSearchText) => {
        setDisplayedContent(content);
    }, []);

    useEffect(() => {
        if (!setSearchTarget) return undefined;

        setSearchTarget({ text: parsedAndSanitizedContent, updateContent: handleContentUpdate });

        return () => setSearchTarget(undefined);
    }, [handleContentUpdate, parsedAndSanitizedContent, setSearchTarget]);

    useEffect(() => {
        // When search is activated, DocumentSearch component handles the content update
        if (!withSearch) {
            setDisplayedContent(parsedAndSanitizedContent);
        }
    }, [parsedAndSanitizedContent, withSearch]);

    useEffect(() => {
        if (scrolledHalfway) {
            track({
                name: TrackingEventName.DOCUMENT_READ,
                params: {
                    reading_state: 'half',
                    screen_name: pathname,
                },
            });
        }
    }, [track, pathname, scrolledHalfway]);

    useEffect(() => {
        if (scrolledToBottom) {
            track({
                name: TrackingEventName.DOCUMENT_READ,
                params: {
                    reading_state: 'full',
                    screen_name: pathname,
                },
            });
        }
    }, [track, pathname, scrolledToBottom]);

    useEffect(() => {
        setTimeout(
            () =>
                track({
                    name: TrackingEventName.DOCUMENT_READ,
                    params: {
                        reading_state: 'minute',
                        screen_name: pathname,
                    },
                }),
            60000,
        );
    }, [track, pathname]);

    return (
        <Box sx={sxs([{ mt: 3 }, containerSx])}>
            {withSearch && (
                <DocumentSearch
                    controls={searchControls}
                    targetId={searchTargetId}
                    text={searchTarget?.text ?? parsedAndSanitizedContent}
                    updateContent={searchTarget?.updateContent ?? handleContentUpdate}
                />
            )}
            <Typography
                component="div"
                data-test-id={dataTestId}
                sx={sxs([contentSx, contentSxOverride])}
                variant="body1"
            >
                {displayedContent}
            </Typography>
        </Box>
    );
};

export default DisplayFullText;
